"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  FolderKanban,
  ListTodo,
  Pin,
  X,
} from "lucide-react";
import { PROJECT_STATUSES, STATUSES } from "@/lib/constants";
import { assignedOnboardingTasks } from "@/lib/onboarding";
import { relevantHomeDocs, relevantHomeProjects } from "@/lib/workspaceHome";
import { useStore } from "@/lib/store";
import { formatRelative } from "@/lib/utils";
import { Modal } from "@/components/ui/Modal";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PautaMark } from "@/components/brand/PautaMark";

const STEPS = [
  { label: "Projects", icon: FolderKanban },
  { label: "My tasks", icon: ListTodo },
  { label: "Docs", icon: FileText },
] as const;

type OnboardingDestination = "projects" | "my-issues" | "docs";

export function PautaOnboarding({
  open,
  workspaceId,
  workspaceName,
  currentUserId,
  onPersistCompletion,
  onDismiss,
  onExplore,
}: {
  open: boolean;
  workspaceId: string;
  workspaceName: string;
  currentUserId: string;
  onPersistCompletion: () => Promise<boolean>;
  onDismiss: () => void;
  onExplore: (view: OnboardingDestination) => void;
}) {
  const issues = useStore((state) => state.issues);
  const projects = useStore((state) => state.projects);
  const docs = useStore((state) => state.docs);
  const [step, setStep] = useState(0);
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  const workspaceIssues = useMemo(
    () => issues.filter((issue) => issue.workspaceId === workspaceId),
    [issues, workspaceId]
  );
  const workspaceProjects = useMemo(
    () => projects.filter((project) => project.workspaceId === workspaceId),
    [projects, workspaceId]
  );
  const workspaceDocs = useMemo(
    () => docs.filter((doc) => doc.workspaceId === workspaceId),
    [docs, workspaceId]
  );
  const projectItems = useMemo(
    () => relevantHomeProjects(workspaceProjects, workspaceIssues, currentUserId),
    [currentUserId, workspaceIssues, workspaceProjects]
  );
  const taskItems = useMemo(
    () => assignedOnboardingTasks(workspaceIssues, currentUserId),
    [currentUserId, workspaceIssues]
  );
  const relevantProjectIds = useMemo(
    () => new Set(projectItems.map((project) => project.id)),
    [projectItems]
  );
  const docItems = useMemo(
    () => relevantHomeDocs(workspaceDocs, relevantProjectIds, currentUserId),
    [currentUserId, relevantProjectIds, workspaceDocs]
  );

  useEffect(() => {
    if (!open) return;
    setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement;
    return () => {
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        primaryActionRef.current?.focus();
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [open, step]);

  function dismiss(destination?: OnboardingDestination) {
    onDismiss();
    if (destination) onExplore(destination);
    void onPersistCompletion().catch(() => false);
  }

  function next() {
    if (step < STEPS.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    dismiss();
  }

  function trapFocus(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute("hidden"));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!dialogRef.current.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <Modal open={open} onClose={() => dismiss()} width={640}>
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pauta-onboarding-title"
        className="flex min-h-0 flex-1 flex-col"
        onKeyDown={trapFocus}
      >
        <header className="flex items-center gap-3 border-b border-[var(--border)] px-5 py-4">
          <PautaMark size={32} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <h2
              id="pauta-onboarding-title"
              className="truncate text-[15px] font-medium text-[var(--fg)]"
            >
              Welcome to {workspaceName}
            </h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--fg-tertiary)]">
              Three places are all you need to get started in Pauta.
            </p>
          </div>
          <button
            type="button"
            onClick={() => dismiss()}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] focus-ring"
            aria-label="Close tour"
          >
            <X size={15} />
          </button>
        </header>

        <div className="grid grid-cols-3 border-b border-[var(--border)] bg-[var(--bg-secondary)] px-2">
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            const active = step === index;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() => setStep(index)}
                className={`relative flex items-center justify-center gap-1.5 px-2 py-3 text-[12px] font-medium transition-colors focus-ring ${
                  active
                    ? "text-[var(--fg)]"
                    : "text-[var(--fg-muted)] hover:text-[var(--fg-secondary)]"
                }`}
                aria-current={active ? "step" : undefined}
              >
                <Icon size={13} />
                <span>{item.label}</span>
                {active && (
                  <motion.span
                    layoutId="onboarding-step"
                    className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-[var(--accent)]"
                    transition={{ type: "spring", stiffness: 320, damping: 32 }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="px-5 py-5 md:px-6 md:py-6"
            >
              {step === 0 ? (
                <ProjectsStep
                  projects={projectItems.slice(0, 3)}
                  total={projectItems.length}
                  onExplore={() => dismiss("projects")}
                />
              ) : step === 1 ? (
                <TasksStep
                  tasks={taskItems.slice(0, 3)}
                  total={taskItems.length}
                  onExplore={() => dismiss("my-issues")}
                />
              ) : (
                <DocsStep
                  docs={docItems.slice(0, 3)}
                  total={workspaceDocs.length}
                  onExplore={() => dismiss("docs")}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <footer className="border-t border-[var(--border)] bg-[var(--bg-secondary)] px-5 py-3">
          <div className="flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <button
              type="button"
              onClick={() => dismiss()}
              className="w-full px-2 py-1.5 text-[12px] font-medium text-[var(--fg-muted)] transition-colors hover:text-[var(--fg-secondary)] focus-ring sm:w-auto"
            >
              Skip tour
            </button>
            <div className="flex items-center justify-end gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={() => setStep((current) => current - 1)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] focus-ring"
                >
                  <ArrowLeft size={12} />
                  Back
                </button>
              )}
              <button
                ref={primaryActionRef}
                type="button"
                onClick={next}
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[var(--accent-hover)] focus-ring"
              >
                <ArrowRight size={12} />
                {step === STEPS.length - 1 ? "Start using Pauta" : "Next"}
              </button>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}

function StepIntro({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <div className="mb-5 max-w-[540px]">
      <div className="text-[10.5px] font-medium uppercase tracking-wider text-[var(--accent)]">
        {eyebrow}
      </div>
      <h3 className="mt-1.5 text-[21px] font-medium leading-7 tracking-[-0.02em] text-[var(--fg)]">
        {title}
      </h3>
      <p className="mt-2 text-[13px] leading-5 text-[var(--fg-tertiary)]">{body}</p>
    </div>
  );
}

function PreviewShell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)]">
      <div className="border-b border-[var(--border)] px-3 py-2 text-[10.5px] font-medium uppercase tracking-wide text-[var(--fg-muted)]">
        {label}
      </div>
      <div className="divide-y divide-[var(--border)]">{children}</div>
    </div>
  );
}

function ProjectsStep({
  projects,
  total,
  onExplore,
}: {
  projects: ReturnType<typeof relevantHomeProjects>;
  total: number;
  onExplore: () => void;
}) {
  return (
    <>
      <StepIntro
        eyebrow="1 of 3 · Projects"
        title="Projects tell the whole story"
        body="Goals, live work, progress, docs, and recent agent activity stay together, so everyone can see what is moving and what needs attention."
      />
      <PreviewShell label={`${total} active ${total === 1 ? "project" : "projects"}`}>
        {projects.length ? (
          projects.map((project) => {
            const status = PROJECT_STATUSES.find((item) => item.id === project.status);
            return (
              <div key={project.id} className="flex items-center gap-3 px-3 py-3">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-[15px]"
                  style={{
                    borderColor: `${project.color}66`,
                    background: `${project.color}18`,
                    color: project.color,
                  }}
                >
                  {project.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-[var(--fg)]">
                    {project.name}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-[var(--fg-muted)]">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: status?.color }} />
                    {status?.name ?? "Project"}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <EmptyPreview icon={FolderKanban} text="Your workspace's active projects will appear here." />
        )}
      </PreviewShell>
      <ExploreButton label="Open Projects" onClick={onExplore} />
    </>
  );
}

function TasksStep({
  tasks,
  total,
  onExplore,
}: {
  tasks: ReturnType<typeof assignedOnboardingTasks>;
  total: number;
  onExplore: () => void;
}) {
  return (
    <>
      <StepIntro
        eyebrow="2 of 3 · My tasks"
        title="My tasks is your personal queue"
        body="Every open task assigned to you lands here. Work an agent sends back for your approval stays separate in Awaiting me."
      />
      <PreviewShell label={`${total} open ${total === 1 ? "task" : "tasks"} assigned to you`}>
        {tasks.length ? (
          tasks.map((task) => {
            const status = STATUSES.find((item) => item.id === task.status);
            return (
              <div key={task.id} className="flex items-center gap-3 px-3 py-3">
                <StatusIcon status={task.status} size={15} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-[var(--fg)]">
                    {task.title}
                  </div>
                  <div className="mt-0.5 text-[10.5px] text-[var(--fg-muted)]">
                    {task.identifier} · {status?.name}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <EmptyPreview icon={ListTodo} text="Tasks assigned to you will appear here." />
        )}
      </PreviewShell>
      <ExploreButton label="Open My tasks" onClick={onExplore} />
    </>
  );
}

function DocsStep({
  docs,
  total,
  onExplore,
}: {
  docs: ReturnType<typeof relevantHomeDocs>;
  total: number;
  onExplore: () => void;
}) {
  return (
    <>
      <StepIntro
        eyebrow="3 of 3 · Docs"
        title="Docs keep context close to the work"
        body="Use Docs for specs, decisions, meeting notes, and plans. Link them to projects so the team and its agents always share the same context."
      />
      <PreviewShell label={`${total} workspace ${total === 1 ? "doc" : "docs"}`}>
        {docs.length ? (
          docs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-3 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] text-[15px]">
                {doc.icon || "📄"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px] font-medium text-[var(--fg)]">
                  {doc.title || "Untitled"}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-[10.5px] text-[var(--fg-muted)]">
                  {doc.pinned && <Pin size={9} />}
                  {doc.pinned ? "Pinned" : `Updated ${formatRelative(doc.updatedAt)}`}
                </div>
              </div>
            </div>
          ))
        ) : (
          <EmptyPreview icon={FileText} text="Specs, decisions, and meeting notes will appear here." />
        )}
      </PreviewShell>
      <ExploreButton label="Open Docs" onClick={onExplore} />
    </>
  );
}

function ExploreButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] font-medium text-[var(--accent)] transition-colors hover:bg-[var(--accent-bg)] focus-ring"
    >
      {label}
      <ArrowRight size={12} />
    </button>
  );
}

function EmptyPreview({
  icon: Icon,
  text,
}: {
  icon: typeof FolderKanban;
  text: string;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-5 text-[12px] text-[var(--fg-muted)]">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--bg-tertiary)]">
        <Icon size={14} />
      </span>
      {text}
    </div>
  );
}
